import { Tag } from 'antd';
import apiFundDistributorProductsListCall from 'apps/investcore/apiCalls/fund-distributor-products/list';
import apiFundProductFeesCall from 'apps/investcore/apiCalls/fund-product-fees/list';
import {
  productStatusColors,
  productStatusLabels,
} from 'apps/investcore/constants/fundProduct';
import { DataTable } from 'common/data-table';
import ErrorMessage from 'common/ui/ErrorMessage';
import InfoCard from 'common/ui/info-card';
import { useFetchApiList } from 'hooks/api';
import { useEffect } from 'react';
import { ExternalLinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency, formatNumber } from 'utils/formatAmount';
import { formatDate, formatDateTime } from 'utils/formatDate';
import { cloudflareCdn } from 'utils/image';
import { columnsGenerator as columnsGeneratorFundDistributorProducts } from '../fund-distributor-products/config';
import { columnsGenerator as columnsGeneratorFundProductFees } from '../fund-product-fees/config';

const ItemDetail = ({ item }) => {
  const {
    id,
    code,
    alias,
    name,
    fund_manager,
    fund_manager_name,
    description,
    fund_type,
    fund_type_name,
    status,
    logo,
    order_closing_time,
    receive_money_closed_time,
    order_time,
    inception_date,
    trading_date,
    risk_level,
    risk_level_label,
    prospectus_url,
    policy_url,
    redemption_payment_term,
    custodian_bank,
    registration_certificate,
    strategy,
    supervisory_bank,
    audit_company,
    management_fee,
    created_at,
    updated_at,
    fmarket_product_id,
    max_subscription_amount,
    min_holding_volume,
    min_subscription_amount,
    min_redemption_volume,
    nav,
    nav_date,
    nav_last_updated,
  } = item || {};

  const { data: fundProductFees, error: errorFundProductFees, load: loadFundProductFees } = useFetchApiList(
    apiFundProductFeesCall,
    { resourceName: 'data' }
  );
  const {
    data: fundDistributorProducts,
    error: errorFundDistributorProducts,
    load: loadFundDistributorProducts,
  } = useFetchApiList(apiFundDistributorProductsListCall, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (id) {
      loadFundProductFees({ fund_product_id: id });
      loadFundDistributorProducts({ fund_product_id: id });
    }
  }, [id]);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex gap-4">
            {logo ? (
              <img
                src={cloudflareCdn(logo, 100)}
                alt="Logo"
                className="w-14 rounded object-contain"
              />
            ) : null}
            <InfoCard className="flex-grow" label="Code" value={code} />
          </div>
          <InfoCard className="flex-grow" label="Alias" value={alias} />
          <InfoCard
            className="flex-grow"
            label="Status"
            value={
              <Tag color={productStatusColors[status]}>
                {productStatusLabels[status] || status}
              </Tag>
            }
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InfoCard className="col-span-2" label="Name" value={name} />
          <InfoCard
            label="Risk Level"
            value={risk_level ? risk_level_label || risk_level : '-'}
          />
          <InfoCard
            label="Fund Type"
            value={`${fund_type_name} (${fund_type})`}
          />
          <InfoCard
            label="Fund Manager"
            value={
              item?.fund ? (
                <Link to={`/admin/fund-managers/${item.fund.id}`}>
                  {`${item.fund.name} (${item.fund.code})`}
                </Link>
              ) : (
                `${fund_manager_name} (${fund_manager})`
              )
            }
          />
          <InfoCard label="FMarket Product ID" value={fmarket_product_id} />
        </div>

        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          NAV
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Nav" value={formatNumber(nav)} />
          <InfoCard label="Nav date" value={formatDateTime(nav_date)} />
          <InfoCard
            label="Nav last updated"
            value={formatDateTime(nav_last_updated)}
          />
        </div>

        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          Thông tin giao dịch
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard
            label="Max subscription amount"
            value={formatCurrency(max_subscription_amount)}
          />
          <InfoCard
            label="Min subscription amount"
            value={formatCurrency(min_subscription_amount)}
          />
          <InfoCard
            label="Management fee"
            value={formatCurrency(management_fee)}
          />
          <InfoCard
            label="Min holding volume"
            value={formatNumber(min_holding_volume)}
          />
          <InfoCard
            label="Min redemption volume"
            value={formatNumber(min_redemption_volume)}
          />
          <InfoCard
            label="Redemption payment term"
            value={formatNumber(redemption_payment_term)}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoCard label="Trading Date" value={trading_date} />

          <InfoCard label="Order Time" value={order_time} />
          <InfoCard label="Order Closing Time" value={order_closing_time} />
          <InfoCard
            label="Receive money closed time"
            value={receive_money_closed_time}
          />
        </div>

        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          Ngân hàng
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoCard
            label="Custodian Bank"
            value={
              custodian_bank && (
                <Link
                  to={`/admin/banks/${custodian_bank?.id}`}
                >{`${custodian_bank?.name} - ${custodian_bank?.code}`}</Link>
              )
            }
          />
          <InfoCard
            label="Supervisory Bank"
            value={
              supervisory_bank && (
                <Link
                  to={`/admin/banks/${supervisory_bank?.id}`}
                >{`${supervisory_bank?.name} - ${supervisory_bank?.code}`}</Link>
              )
            }
          />
        </div>

        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          Thông tin quỹ
        </h3>
        <div className="space-y-2">
          <InfoCard label="Description" value={description} />
          <InfoCard label="Strategy" value={strategy} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InfoCard
            label="Prospectus URL"
            value={
              prospectus_url ? (
                <a
                  href={prospectus_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLinkIcon className="w-4 h-4" />
                  View Prospectus
                </a>
              ) : (
                '-'
              )
            }
          />
          <InfoCard
            label="Policy URL"
            value={
              policy_url ? (
                <a
                  href={policy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLinkIcon className="w-4 h-4" />
                  View Policy
                </a>
              ) : (
                '-'
              )
            }
          />
          <InfoCard
            label="Registration Certificate"
            value={registration_certificate}
          />
          <InfoCard label="Inception Date" value={formatDate(inception_date)} />
          <InfoCard label="Audit Company" value={audit_company} />
        </div>
      </div>

      {/* Metadata Section */}
      <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Created At" value={formatDateTime(created_at)} />
          <InfoCard label="Updated At" value={formatDateTime(updated_at)} />
        </div>
      </div>

      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
        Biểu phí
      </h3>
      {!!errorFundProductFees ? (
        <ErrorMessage errors={errorFundProductFees} />
      ) : (
        <DataTable
          noViewOptions
          data={fundProductFees || []}
          columns={columnsGeneratorFundProductFees()}
        />
      )}

      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
        Danh sách phân phối
      </h3>
      {!!errorFundDistributorProducts ? (
        <ErrorMessage errors={errorFundDistributorProducts} />
      ) : (
        <DataTable
          noViewOptions
          data={fundDistributorProducts || []}
          columns={columnsGeneratorFundDistributorProducts()}
        />
      )}
    </div>
  );
};

export default ItemDetail;
